/**
 * Service Slug Mapping
 * Maps internal service IDs to localized URL slugs for Romanian and Russian
 *
 * Structure: Internal ID → { ro: Romanian slug, ru: Russian slug }
 *
 * NOTE: RO/RU slugs are placeholders pending the per-market keyword-SEO phase;
 * refine them to match Moldovan search terms, then update vercel.json redirects
 * and regenerate routes/sitemap.
 */

export type ServiceId =
  | 'managed-it-support'
  | 'cloud-infrastructure'
  | 'cybersecurity-identity'
  | 'data-databases'
  | 'ai-automation'
  | 'web-software-development'
  | 'mobile-app-development'
  | 'crm-development'
  | 'erp-systems';

export interface ServiceSlugMap {
  ro: string;
  ru: string;
}

/**
 * Service slug mappings
 * Internal IDs map to localized slugs
 */
export const serviceSlugMap: Record<ServiceId, ServiceSlugMap> = {
  'managed-it-support': {
    ro: 'externalizare-it',
    ru: 'it-autsorsing'
  },
  'cloud-infrastructure': {
    ro: 'servicii-cloud',
    ru: 'oblachnye-uslugi'
  },
  'cybersecurity-identity': {
    ro: 'securitate-cibernetica',
    ru: 'kiberbezopasnost'
  },
  'data-databases': {
    ro: 'baze-de-date',
    ru: 'bazy-dannykh'
  },
  'ai-automation': {
    ro: 'automatizare-ai',
    ru: 'ii-avtomatizatsiya'
  },
  'web-software-development': {
    ro: 'creare-site-web',
    ru: 'razrabotka-saytov'
  },
  'mobile-app-development': {
    ro: 'dezvoltare-aplicatii-mobile',
    ru: 'razrabotka-mobilnykh-prilozheniy'
  },
  'crm-development': {
    ro: 'sisteme-crm',
    ru: 'crm-sistemy'
  },
  'erp-systems': {
    ro: 'sisteme-erp',
    ru: 'erp-sistemy'
  }
};

/**
 * Reverse mappings for URL parsing
 */
const createReverseMap = (): Record<string, ServiceId> => {
  const reverseMap: Record<string, ServiceId> = {};

  Object.entries(serviceSlugMap).forEach(([id, slugs]) => {
    reverseMap[slugs.ro] = id as ServiceId;
    reverseMap[slugs.ru] = id as ServiceId;
    // Also keep the original English ID for backward compatibility during migration
    reverseMap[id] = id as ServiceId;
  });

  return reverseMap;
};

export const reverseServiceSlugMap = createReverseMap();

/**
 * Get localized service slug
 */
export function getLocalizedServiceSlug(serviceId: ServiceId, language: 'ro' | 'ru'): string {
  return serviceSlugMap[serviceId]?.[language] || serviceId;
}

/**
 * Get service ID from localized slug
 */
export function getServiceIdFromSlug(slug: string): ServiceId | null {
  return reverseServiceSlugMap[slug] || null;
}

/**
 * Get all service IDs
 */
export function getAllServiceIds(): ServiceId[] {
  return Object.keys(serviceSlugMap) as ServiceId[];
}

/**
 * Get all localized service slugs for a language
 */
export function getAllLocalizedServiceSlugs(language: 'ro' | 'ru'): string[] {
  return Object.values(serviceSlugMap).map(slugs => slugs[language]);
}
